import React from "react";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import AreaStatsChart from "@/shared/components/dashboard/TransactionChart";

export default function OverviewTab({
  merchant,
  recentTransactions,
  txLoading,
  onView,
  chartData,
  chartCurrencySymbol,
  t,
}) {
  const feeRate = (merchant?.fee_rate ?? merchant?.feeRate) != null ? (merchant.fee_rate ?? merchant.feeRate) : null;
  const feeType = (merchant?.fee_type || merchant?.feeType || "PERCENTAGE").toUpperCase();
  let feeLabel;
  if (feeRate == null) {
    feeLabel = t ? t("nA") : "Not available";
  } else if (feeType === "FIXED") {
    feeLabel = `${feeRate} ${merchant?.currency || ""}`.trim();
  } else {
    feeLabel = `${feeRate}%`;
  }

  const merchantInfoFields = [
    { label: t ? t("currency") : "Currency", value: merchant?.currency || "THB" },
    { label: t ? t("settlementPeriod") : "Settlement Period", value: merchant?.settlementPeriod || "N/A" },
    { label: t ? t("fee") : "Fee", value: feeLabel },
    { label: t ? t("callbackUrl") : "Callback URL", value: merchant?.callbackUrl || "N/A", breakAll: true },
    { label: t ? t("webhookUrl") : "Webhook URL", value: merchant?.webhookUrl || "N/A", breakAll: true, small: true },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Volume Chart (last 7 days) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="lg:col-span-2 h-full"
      >
        <AreaStatsChart
          data={chartData}
          currency={chartCurrencySymbol}
          title={t ? t("volumeTrend7Days") : "Volume Trend (7 Days)"}
        />
      </motion.div>

      {/* Quick Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="h-full"
      >
        <Card className="h-full flex flex-col">
          <CardHeader>
            <CardTitle>{t ? t("merchantInfo") : "Merchant Info"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 flex-1">
            {merchantInfoFields.map((field) => (
              <div key={field.label}>
                <p className="text-sm text-slate-500">{field.label}</p>
                <p className={`${field.small ? "text-sm" : "font-medium"} ${field.breakAll ? "break-all" : ""}`}>
                  {field.value}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Transactions (latest 5) */}
      <div className="lg:col-span-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t ? t("recentTransactions") : "Recent Transactions"}</CardTitle>
          </CardHeader>
          <CardContent>
            <AdminTransactionTable
              transactions={recentTransactions}
              isLoading={txLoading}
              onView={onView}
              showMerchant={false}
              showReseller={false}
              mixedTypes={true}
              compact
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
